import { ReactElement, useEffect, useState } from 'react';
import { Field, FormikProvider, useFormik } from 'formik';
import { Link } from 'react-router';
import moment from 'moment';
import { KeyboardBackspaceRounded } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import Grid from '@mui/material/Grid';
import { CircularProgress } from '@mui/material';

// Components
import DatePicker from 'components/DatePicker/DatePicker';
import InputBlock from 'components/global/inputBlock/inputBlock';
import Button from 'components/Button/Button';
import { Modal } from 'components/ContinuousImprovement/Modal';

import { FormikTextField } from 'components/ContinuousImprovement/FormikTextField';
import { ITypeaheadOption, Typeahead } from 'components/ContinuousImprovement/Typeahead';
import { IMatchedLearnerOption, LearnerAutocompleteInput } from './LearnerAutocompleteInput';

// Store + Core
import { statesAndProvincesSelector } from 'store/locations/selectors';
import { medicalSchoolTermsSelector } from 'store/taxonomy/selectors';
import { boardListSelector } from 'store/bct/selectors';
import { matchedLearnersSelector } from 'store/learner/selectors';
import { matchLearners } from 'store/learner/actions';
import { closeModal, openModal } from 'store/modal/actions';
import { getStatesAndProvinces } from 'store/locations/actions';
import { getTaxonomyTermById } from 'store/taxonomy/actions';
import { getBoardList } from 'store/bct/actions';
import {
  AlphanumericLearnerMatchBoards,
  IBoardSlim,
  IDictionary,
  ILearnerIdMatchField,
  ILearnerMatchingModel,
  IMatchedLearner,
  IStateAndProvince,
  ITaxonomyTerm,
  IValidateLearnersToolFormModel,
  PARSAction,
} from 'core/models';
import { ButtonVariant, StatusEnum } from 'core/enums';
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'store';
import { normalizeString, stringHasLetters } from 'utils';

// constants
import { TAXONOMY_MEDICAL_SCHOOLS_ROOT_ID } from 'core/constants';

// Styles
import styles from './LearnerValidationForm.module.scss';

// Validations.
import { LearnerValidationToolSchema } from '../validationSchemas';

import { LearnerValidationToolResult } from 'components/ContinuousImprovement/Modal/templates/learner-validation-tool-result';

const initialValues: IValidateLearnersToolFormModel = {
  boardId: '',
  boardName: undefined,
  dateOfBirth: undefined,
  firstName: '',
  lastName: '',
  licenseId: '',
  medicalSchoolName: undefined,
  npi: '',
  stateName: undefined,
};

export const LearnerValidationToolForm = (): ReactElement => {
  // local state

  const [titleSummary, setTitleSummary] = useState<string>('');
  const [modalButtonText, setModalButtonText] = useState<string>('');

  const dispatch: ThunkDispatch<AppState, unknown, PARSAction> = useDispatch();

  const testDateIsValid = (date: Date): boolean => !!new Date(date).getDate();

  const splitDateAsDob = (dateMoment: moment.Moment) => [dateMoment.month() + 1, dateMoment.date()];

  const getMatchPayloadFromFormValues = ({
    boardId,
    boardName,
    dateOfBirth,
    firstName,
    lastName,
    licenseId,
    npi,
    stateName,
  }: IValidateLearnersToolFormModel): ILearnerMatchingModel => {
    const boardIds: IDictionary<string> = {};

    // if user provides board ids(combination of boardId and LearnerId), use those; otherwise use boardId as licenseId which is mapped with learnerId as per learner match api.

    if (boardName && boardName.id) {
      boardIds[boardName.id] = boardId || '';
    } else if (!licenseId && boardId) {
      licenseId = boardId;
    }

    // If the Date constructor fails to make a valid date, this will be false
    const isDobValidDateString = testDateIsValid(dateOfBirth);
    const [birthMonth, birthDay] = dateOfBirth && isDobValidDateString ? splitDateAsDob(moment(dateOfBirth)) : [];

    const payload: ILearnerMatchingModel = {
      birthDay,
      birthMonth,
      firstName,
      lastName,
      licenseId,
      npi,
      stateName: stateName?.label,
    };

    // Only include boardIds if there are any entries
    if (Object.keys(boardIds).length > 0) {
      payload.boardIds = boardIds;
    }

    return payload;
  };

  const matchedLearners: IMatchedLearner[] = useSelector(matchedLearnersSelector);
  const [isLearnerValidationModalOpen, setIsLearnerValidationModalOpen] = useState<boolean>(false);
  // Hooks.
  const medicalSchools: ITaxonomyTerm[] = useSelector(medicalSchoolTermsSelector);

  const statesAndProvinces: IStateAndProvince[] = useSelector(statesAndProvincesSelector);

  const boardList: IBoardSlim[] = useSelector(boardListSelector);

  const boardListOptions: ITypeaheadOption[] =
    boardList
      ?.filter((board) => board.learnerStatus === StatusEnum.ACTIVE)
      .map(
        ({ id, name }: IBoardSlim): ITypeaheadOption => ({
          id: id,
          label: name,
        }),
      ) || [];

  const statesAndProvincesOptions: ITypeaheadOption[] =
    statesAndProvinces?.map(
      ({ isoStateCode, stateName }: IStateAndProvince): ITypeaheadOption => ({
        id: isoStateCode,
        label: stateName,
      }),
    ) || [];

  useEffect(() => {
    if (!statesAndProvinces) {
      dispatch(getStatesAndProvinces());
    }
  }, [statesAndProvinces, dispatch]);

  useEffect(() => {
    if (!medicalSchools) {
      dispatch(getTaxonomyTermById(TAXONOMY_MEDICAL_SCHOOLS_ROOT_ID));
    }
  }, [medicalSchools, dispatch]);

  useEffect(() => {
    if (!boardList) {
      dispatch(getBoardList());
    }
  }, [boardList, dispatch]);

  const handleBaseLearnerMatching = async (learnerFormValues: IValidateLearnersToolFormModel): Promise<void> => {
    const matchingPayload: ILearnerMatchingModel = getMatchPayloadFromFormValues(learnerFormValues);

    await dispatch(matchLearners(matchingPayload));
  };

  const formik = useFormik({
    initialValues,

    onSubmit: async (values): Promise<void> => {
      await handleBaseLearnerMatching(values);

      setIsLearnerValidationModalOpen(true);
      dispatch(openModal());
    },
    validateOnChange: true,
    validationSchema: LearnerValidationToolSchema,
  });

  const { dirty, errors, handleSubmit, isSubmitting, isValid, resetForm, touched, values } = formik;

  const sortedMedicalSchools: ITypeaheadOption[] =
    medicalSchools?.map(({ id, name }: ITaxonomyTerm): ITypeaheadOption => ({ id, label: name })) || [];

  let isInvalidAlphanumericMatch: boolean = false;

  /**
   * Populate the values that will be displayed in the modal.
   */
  const getModalDisplayValues = () => {
    if (matchedLearners?.length === 1) {
      const matched = matchedLearners[0];

      // Prefer the state board matching the user's input; fall back to first
      const userStateAbbr = values.stateName?.id;
      const stateBoard = userStateAbbr
        ? (matched.learnerStateBoards?.find((sb) => sb.abbreviation === userStateAbbr) ??
          matched.learnerStateBoards?.[0])
        : matched.learnerStateBoards?.[0];

      // check if any of the MOC boards match an alphanumeric learner match board
      let mocBoard: ILearnerIdMatchField;
      const boardGuid: string = values.boardName?.id;
      const matchedBoardId: string = values.boardId;
      const hasAlphanumericBoard: boolean = matched.learnerMocBoards?.some((board) =>
        Object.values(AlphanumericLearnerMatchBoards).map(normalizeString).includes(normalizeString(board.name)),
      );
      const hasAlphanumericInput = Object.values(AlphanumericLearnerMatchBoards)
        .map(normalizeString)
        .includes(normalizeString(values.boardName?.label));

      if (hasAlphanumericBoard && hasAlphanumericInput) {
        if (stringHasLetters(matchedBoardId)) {
          mocBoard = matched.learnerMocBoards.find(
            (board) => board.learnerId === matchedBoardId && boardGuid === board.boardId,
          );
        } else {
          isInvalidAlphanumericMatch = true;
          return values;
        }
      } else {
        mocBoard = matched.learnerMocBoards?.find((board) => board.boardId === boardGuid);
      }

      const resolvedStateName = stateBoard
        ? (statesAndProvincesOptions.find(
            (opt) => opt.id === stateBoard.abbreviation || opt.label === stateBoard.name,
          ) ?? { id: stateBoard.abbreviation ?? '', label: stateBoard.name })
        : undefined;

      const resolvedBoardName = mocBoard
        ? (boardListOptions.find((opt) => opt.id === mocBoard.boardId) ?? {
            id: mocBoard.boardId ?? '',
            label: mocBoard.name,
          })
        : undefined;

      const resolvedMedicalSchool = matched.medicalSchoolName
        ? (sortedMedicalSchools.find(
            (s) => s.label.trim().toLowerCase() === matched.medicalSchoolName.trim().toLowerCase(),
          ) ?? { id: '', label: matched.medicalSchoolName })
        : undefined;

      return {
        boardId: mocBoard?.learnerId ?? '',
        boardName: resolvedBoardName,
        dateOfBirth: matched.dateOfBirth,
        firstName: matched.firstName,
        lastName: matched.lastName,
        licenseId: stateBoard?.learnerId ?? '',
        medicalSchoolName: resolvedMedicalSchool,
        npi: matched.npi,
        stateName: resolvedStateName,
      };
    }
    return values;
  };

  const isSubmitDisabled: boolean = !dirty || isSubmitting || !isValid;

  useEffect(() => {
    if (matchedLearners) {
      if (matchedLearners?.length === 0 || isInvalidAlphanumericMatch) {
        setTitleSummary(
          'There is no learner matching the information you provided. Please try the validation again by entering more data about the learner to see if a match can be made.',
        );
        setModalButtonText('Try again with more information');
      } else if (matchedLearners?.length === 1) {
        setModalButtonText('Done');
        setTitleSummary('The learner is matched, and you can submit completion data for this learner.');
      } else {
        setModalButtonText('Try again with more information');
        setTitleSummary(
          'Multiple learners were matched to the data you provided. Please try the validation again by entering more data about the learner to see if a single match can be made.',
        );
      }
    }
  }, [matchedLearners, isInvalidAlphanumericMatch]);

  const closeValidateLearnersModal = (): void => {
    if (matchedLearners?.length === 1 && !isInvalidAlphanumericMatch) {
      resetForm({ errors, touched, values: initialValues });
    }
    setIsLearnerValidationModalOpen(false);
    dispatch(closeModal());
  };

  /**
   * Handles changes when a new learner is selected.
   * @param learner
   */
  const handleLearnerSelect = (learner: IMatchedLearnerOption) => {
    // Populate form fields based on the selected learner
    const updates: Partial<IValidateLearnersToolFormModel> = {
      dateOfBirth: learner.dateOfBirth,
      firstName: learner.firstName,
      lastName: learner.lastName,
      npi: learner.npi,
    };

    // Try to match medical school
    if (learner.medicalSchoolName) {
      const learnerSchoolName = learner.medicalSchoolName.trim().toLowerCase();
      const foundSchool = sortedMedicalSchools.find((s) => s.label.trim().toLowerCase() === learnerSchoolName);
      if (foundSchool) {
        updates.medicalSchoolName = foundSchool;
      }
    }

    // Use matchedOption from the flattened option for state/license
    if (learner.matchedOption) {
      const sb = learner.matchedOption;
      const foundState = statesAndProvincesOptions.find((opt) => opt.id === sb.abbreviation || opt.label === sb.name);
      if (foundState) {
        updates.stateName = foundState;
      }
      if (sb.learnerId) {
        updates.licenseId = sb.learnerId;
      }
    }

    // Populate with the first moc board if available
    if (learner.learnerMocBoards && learner.learnerMocBoards.length > 0) {
      const firstBoard = learner.learnerMocBoards[0];
      const matchedBoard = boardList?.find((b) => b.id === firstBoard.boardId && b.learnerStatus === StatusEnum.ACTIVE);

      if (matchedBoard) {
        updates.boardName = {
          id: matchedBoard.id,
          label: matchedBoard.name,
        };
      }
      if (firstBoard.learnerId) {
        updates.boardId = firstBoard.learnerId;
      }
    }

    formik.setValues({ ...values, ...updates }, true);
  };

  const { firstName, lastName, dateOfBirth, stateName, licenseId, boardName, boardId, npi, medicalSchoolName } =
    getModalDisplayValues();

  return (
    <FormikProvider value={formik}>
      <form onSubmit={handleSubmit}>
        <div className="static-form">
          <div className="form-group">
            <h4>Enter the data that you have for your learner</h4>
            <h6>
              First name and last name are required. Beyond that there are no specific required fields. The more
              information you can provide, the more likely you are to get a single matched learner.
            </h6>
            <Grid container spacing={2} className={styles['form-grid']}>
              <Grid className={styles.input} size={{ sm: 4, xs: 12 }}>
                <InputBlock
                  errors={errors}
                  name="firstName"
                  required
                  suppressLabel
                  touched={touched}
                  title="First name"
                >
                  <LearnerAutocompleteInput
                    name="firstName"
                    placeholder="First name"
                    formValues={values}
                    onSelect={handleLearnerSelect}
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 4, xs: 12 }}>
                <InputBlock errors={errors} name="lastName" required touched={touched} suppressLabel title="Last name">
                  <LearnerAutocompleteInput
                    name="lastName"
                    placeholder="Last name"
                    formValues={values}
                    onSelect={handleLearnerSelect}
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 2, xs: 12 }}>
                <InputBlock errors={errors} name="dateOfBirth" touched={touched} title="Dob" suppressLabel>
                  <Field
                    component={DatePicker}
                    id="dateOfBirth"
                    name="dateOfBirth"
                    className="form-input"
                    format="MM/DD"
                    disablePast={false}
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 5, xs: 12 }}>
                <InputBlock errors={errors} name="stateName" touched={touched} title="Licensing state" suppressLabel>
                  <Typeahead
                    options={statesAndProvincesOptions}
                    id="stateName"
                    name="stateName"
                    placeholder="Select a state or province"
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 5, xs: 12 }}>
                <InputBlock errors={errors} touched={touched} name="licenseId" title="Licensing id" suppressLabel>
                  <LearnerAutocompleteInput
                    name="licenseId"
                    placeholder="Licensing id"
                    formValues={values}
                    onSelect={handleLearnerSelect}
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 6, xs: 12 }}>
                <InputBlock errors={errors} name="boardName" touched={touched} title="Board Name" suppressLabel>
                  <Typeahead
                    options={boardListOptions}
                    id="boardName"
                    name="boardName"
                    placeholder="Enter Board Name"
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 4, xs: 12 }}>
                <InputBlock errors={errors} touched={touched} name="boardId" title="Board Id" suppressLabel>
                  <FormikTextField formikKey="boardId" placeholder="Board Id" variant="outlined" />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 5, xs: 12 }}>
                <InputBlock errors={errors} touched={touched} name="npi" title="NPI" suppressLabel>
                  <LearnerAutocompleteInput
                    name="npi"
                    placeholder="NPI"
                    formValues={values}
                    onSelect={handleLearnerSelect}
                  />
                </InputBlock>
              </Grid>
              <Grid className={styles.input} size={{ sm: 5, xs: 12 }}>
                <InputBlock
                  errors={errors}
                  name="medicalSchoolName"
                  touched={touched}
                  title="Medical school"
                  suppressLabel
                >
                  <Typeahead
                    options={sortedMedicalSchools}
                    id="medicalSchoolName"
                    name="medicalSchoolName"
                    placeholder="Select a medical school"
                  />
                </InputBlock>
              </Grid>
            </Grid>
          </div>
          <div className={styles['button-row']}>
            <Button
              variant={ButtonVariant.Primary}
              title="Validate Learner"
              disabled={isSubmitDisabled}
              onClick={formik.submitForm}
              startIcon={isSubmitting && <CircularProgress color="inherit" size="1rem" />}
            >
              Validate Learner
            </Button>
            <Link className="tertiary" to="/learners/validation/batch">
              Upload a learner validation batch file
              <KeyboardBackspaceRounded className="tertiary-icon" />
            </Link>
          </div>
        </div>
      </form>
      <Modal
        isOpen={isLearnerValidationModalOpen}
        onClose={closeValidateLearnersModal}
        title="Learner Validation Result"
      >
        <LearnerValidationToolResult
          dateOfBirth={dateOfBirth}
          firstName={firstName}
          titleSummary={titleSummary}
          lastName={lastName}
          licenseId={licenseId}
          stateName={stateName}
          boardName={boardName}
          boardId={boardId}
          npi={npi}
          medicalSchoolName={medicalSchoolName}
        />

        {modalButtonText !== '' && (
          <div className={styles['button-row']}>
            <Button
              variant={ButtonVariant.Primary}
              onClick={closeValidateLearnersModal}
              startIcon={isSubmitting && <CircularProgress color="inherit" size="1rem" />}
            >
              {modalButtonText}
            </Button>
          </div>
        )}
      </Modal>
    </FormikProvider>
  );
};
